import AxiosInstance from "@/api/axios";
import {
  defaultPagination,
  PaginationInterface
} from "@/components/Base/Pagination";
import { GeneralFilterType, LeadsDataType } from "@/utils/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface leadsFilterType extends GeneralFilterType {
  type: "Enquiry" | "Tour" | "Trial" | "Lead";
  search?: string | null;
  sort?: string | null;
  order?: string | null;
  currentPage?: number;
  perpage?: number;
  pageLimit: number;
}

let abortController: AbortController | null = null;

export const fetchEnquiryTour = createAsyncThunk(
  "fetchEnquiryTour/fetchData",
  async (
    {
      type,
      organization_id,
      agent,
      branch_id,
      region_id,
      date_range,
      search,
      currentPage,
      perpage,
      pageLimit,
      sort,
      order,
      reengaged
    }: leadsFilterType,
    thunkAPI
  ) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    try {
      const response = await AxiosInstance.get(`/leads/type`, {
        params: {
          type,
          page: currentPage,
          perpage,
          organization_id,
          branch_id,
          region_id,
          date_range,
          pageLimit,
          agent,
          search,
          sort,
          order,
          reengaged
        },
        signal: abortController.signal
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error: any) {
      if (!abortController.signal.aborted || error.code === "ERR_CANCELED") {
        return null;
      }
      return thunkAPI.rejectWithValue(error);
    } finally {
      abortController = null;
    }
  }
);

export const deleteLead = createAsyncThunk(
  'leads/deleteLead',
  async (
    {
      leadId,
      branch,
      region
    }: { leadId: string; region: string; branch: string },
    thunkAPI
  ) => {
    try {
      const response = await AxiosInstance.delete(
        `/leads/type/${leadId}?branch=${branch}&region=${region}`
      );

      return response.data.success
        ? leadId
        : thunkAPI.rejectWithValue("Failed to delete lead");
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "An error occurred");
    }
  }
);

interface LeadsStateType {
  leads: {
    data: LeadsDataType[] | null | undefined;
    all_senders: string[][];
    pagination: PaginationInterface;
    timezone: string;
    showLeadFromColumn: boolean;
  };
  loading: boolean;
  error: string | null;
}

const initialState: LeadsStateType = {
  leads: {
    all_senders: [[]],
    data: null,
    pagination: defaultPagination,
    timezone: "",
    showLeadFromColumn: false
  },
  loading: false,
  error: null
};
const fetchEnquiryTourReducer = createSlice({
  name: "enquiry",
  initialState,
  reducers: {
    updatePagination: (state, action) => {
      state.leads.pagination = {
        ...state.leads.pagination,
        ...action.payload
      };
    },
    updateToursOnAutomationSwitch: (state, action) => {
      const { sender, switchValue } = action.payload;
      if (state.leads.data) {
        const leadIndex = state.leads.data.findIndex(
          (lead) => lead.sender === sender
        );
        if (leadIndex !== -1) {
          state.leads.data[leadIndex].use_automation = switchValue;
        }
      }
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchEnquiryTour.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnquiryTour.fulfilled, (state, action) => {
        state.loading = false;
        state.leads.data = action.payload.data;
        state.leads.pagination = action.payload.pagination;
        state.leads.timezone = action.payload.timezone;
        state.leads.showLeadFromColumn = action.payload.showLeadFromColumn;
      })
      .addCase(fetchEnquiryTour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;

        if (state.leads.data) {
          state.leads.data = state.leads.data.filter(
            (lead) => lead._id !== action.payload
          );
        }
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { updatePagination, updateToursOnAutomationSwitch } =
  fetchEnquiryTourReducer.actions;

export default fetchEnquiryTourReducer.reducer;
